import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Alert,
  Button,
  Typography,
  Divider,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Settings,
  Logout,
  Search as SearchIcon,
  UnfoldMore as UnfoldMoreIcon,
  Check as CheckIcon,
  Add as AddIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';

import { useAuth } from '../../context/AuthContext';
import { useBrand } from '../../context/BrandContext';
import { useNotification } from '../../context/NotificationContext';
import { useThemeMode } from '../../context/ThemeModeContext';
import Sidebar from './Sidebar';
import NotificationPanel from './NotificationPanel';
import MobileBottomNav from './MobileBottomNav';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH, TOPBAR_HEIGHT } from '../../theme';
import { getNavigation, flattenNavigation } from '../../constants/navigation';

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const { user, logout } = useAuth();
  const { selectedBrand, brands, selectBrand } = useBrand();
  const { unreadCount, markAllAsRead } = useNotification();
  const { mode, toggleMode } = useThemeMode();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === '1');
  const [anchorEl, setAnchorEl] = useState(null);
  const [brandMenuAnchor, setBrandMenuAnchor] = useState(null);
  const [notificationOpen, setNotificationOpen] = useState(false);

  const drawerWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      localStorage.setItem('sidebarCollapsed', !prev ? '1' : '0');
      return !prev;
    });
  };

  const navOptions = useMemo(
    () => flattenNavigation(getNavigation({ selectedBrand, user })),
    [selectedBrand, user]
  );

  // Deep links like /brands/4/integrations must select that brand, otherwise
  // the top bar and the brand nav groups stay empty after a page load. Runs
  // only when the brand id in the URL changes, so the top-bar switcher isn't
  // overridden while you stay on the same URL.
  const syncedUrlBrand = useRef(null);
  useEffect(() => {
    const match = location.pathname.match(/^\/brands\/(\d+)(?:\/|$)/);
    if (!match || !brands.length) return;
    const id = Number(match[1]);
    if (syncedUrlBrand.current === id) return;
    const target = brands.find((b) => b.brand_id === id);
    if (!target) return;
    syncedUrlBrand.current = id;
    if (selectedBrand?.brand_id !== id) selectBrand(target);
  }, [location.pathname, brands, selectedBrand, selectBrand]);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location, isMobile]);

  const drawer = (
    <Sidebar
      selectedBrand={selectedBrand}
      user={user}
      onClose={isMobile ? () => setMobileOpen(false) : undefined}
      collapsed={!isMobile && collapsed}
      onToggleCollapse={!isMobile ? toggleCollapse : undefined}
    />
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'background.default',
          color: 'text.primary',
          transition: 'width 0.2s ease, margin-left 0.2s ease',
        }}
      >
        <Toolbar disableGutters sx={{ gap: { xs: 0.5, md: 1.5 }, px: 1.5 }}>
          <IconButton
            color="inherit"
            aria-label={isMobile ? 'Open menu' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            edge="start"
            size="small"
            onClick={() => (isMobile ? setMobileOpen(!mobileOpen) : toggleCollapse())}
          >
            <MenuIcon />
          </IconButton>

          {/* Workspace / brand switcher */}
          <Box
            onClick={(e) => brands.length > 0 && setBrandMenuAnchor(e.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: { xs: 0.5, md: 1 },
              py: 0.5,
              minWidth: 0,
              borderRadius: '6px',
              cursor: brands.length > 0 ? 'pointer' : 'default',
              '&:hover': brands.length > 0 ? { backgroundColor: 'action.hover' } : undefined,
            }}
          >
            <Avatar
              src={selectedBrand?.logo_url}
              sx={{ width: 26, height: 26, fontSize: '0.75rem', bgcolor: 'primary.main' }}
            >
              {selectedBrand?.brand_name?.[0] || 'S'}
            </Avatar>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, maxWidth: { xs: 120, md: 180 } }}>
              {selectedBrand ? selectedBrand.brand_name : 'Select a brand'}
            </Typography>
            {brands.length > 0 && <UnfoldMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
          </Box>

          <Menu anchorEl={brandMenuAnchor} open={Boolean(brandMenuAnchor)} onClose={() => setBrandMenuAnchor(null)}>
            {brands.map((b) => (
              <MenuItem
                key={b.brand_id}
                selected={selectedBrand?.brand_id === b.brand_id}
                onClick={() => { selectBrand(b); setBrandMenuAnchor(null); }}
              >
                <ListItemIcon>
                  <Avatar src={b.logo_url} sx={{ width: 22, height: 22, fontSize: '0.6875rem' }}>{b.brand_name?.[0]}</Avatar>
                </ListItemIcon>
                <ListItemText>{b.brand_name}</ListItemText>
                {selectedBrand?.brand_id === b.brand_id && <CheckIcon fontSize="small" color="primary" />}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem onClick={() => { setBrandMenuAnchor(null); navigate('/brands'); }}>
              <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Manage brands</ListItemText>
            </MenuItem>
          </Menu>

          {/* Quick-jump search across the app's own navigation */}
          <Autocomplete
            size="small"
            options={navOptions}
            groupBy={(o) => o.group}
            getOptionLabel={(o) => o.text}
            onChange={(_, value) => value && navigate(value.path)}
            sx={{ flexGrow: 1, maxWidth: 360, display: { xs: 'none', sm: 'block' } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Jump to a page…"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5 }} />,
                }}
              />
            )}
          />

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              sx={{ p: { xs: 0.75, md: 1.5 } }}
              aria-label={mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              color="inherit"
              onClick={toggleMode}
            >
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>

            <IconButton
              sx={{ p: { xs: 0.75, md: 1.5 } }}
              aria-label="show notifications"
              color="inherit"
              onClick={() => { setNotificationOpen((o) => !o); if (!notificationOpen) markAllAsRead(); }}
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <IconButton
              sx={{ p: { xs: 0.75, md: 1.5 } }}
              edge="end"
              aria-label="account of current user"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }} src={user?.avatar_url} alt={user?.first_name}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem component={RouterLink} to="/profile">
          <Avatar src={user?.avatar_url} sx={{ width: 32, height: 32, mr: 1.5 }} />
          <Box>
            <Typography variant="body2" fontWeight="medium">{user?.first_name} {user?.last_name}</Typography>
            <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem component={RouterLink} to="/profile">
          <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem component={RouterLink} to="/settings">
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={logout}>
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 }, transition: 'width 0.2s ease' }} aria-label="primary navigation">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(!mobileOpen)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: SIDEBAR_WIDTH, backgroundColor: 'background.sidebar' },
          }}
        >
          {drawer}
        </Drawer>

        <Box sx={{ display: { xs: 'none', md: 'block' } }}>{drawer}</Box>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1.5,
          minWidth: 0,
          pb: { xs: 'calc(72px + env(safe-area-inset-bottom))', md: 1.5 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: `${TOPBAR_HEIGHT}px`,
          minHeight: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
          backgroundColor: 'background.default',
          transition: 'width 0.2s ease',
        }}
      >
        {!!user && !user.email_verified && (
          <Alert
            severity="warning"
            sx={{ mb: 1.5 }}
            action={
              <Button size="small" color="inherit" component={RouterLink} to="/verify-email">
                Verify now
              </Button>
            }
          >
            Your email is not verified. Some actions are restricted until verification is complete.
          </Alert>
        )}
        {children}
      </Box>

      <NotificationPanel open={notificationOpen} onClose={() => setNotificationOpen(false)} />
      <MobileBottomNav />
    </Box>
  );
};

export default Layout;
